import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Amount } from "@/components/shared/Amount";
import { PageHeader, Panel, fmtDate } from "@/components/shared/DashKit";
import { useContent } from "@/context/ContentContext";
import { timelineLabel, statusLabel } from "@/lib/labels";
import { StatusBadge } from "@/components/shared/Badges";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Loader2, Check, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const LIVE = ["pending", "processing", "in_progress"];

const TERMINAL_TOASTS = {
  completed: (t) => toast.success(t("client.od.toast.completed_t", "Livraison terminée avec succès"), { description: t("client.od.toast.completed_d", "Votre commande a été livrée intégralement.") }),
  partial: (t) => toast.warning(t("client.od.toast.partial_t", "Livraison partielle"), { description: t("client.od.toast.partial_d", "Une partie de la commande a été livrée, le reste a été remboursé.") }),
  failed: (t) => toast.error(t("client.od.toast.failed_t", "Livraison interrompue"), { description: t("client.od.toast.failed_d", "La commande n'a pas pu être livrée.") }),
  cancelled: (t) => toast.error(t("client.od.toast.cancelled_t", "Livraison annulée"), { description: t("client.od.toast.cancelled_d", "La commande a été annulée.") }),
  refunded: (t) => toast.info(t("client.od.toast.refunded_t", "Commande remboursée"), { description: t("client.od.toast.refunded_d", "Le montant a été recrédité sur votre portefeuille.") }),
};

export default function OrderDetail() {
  const { t } = useContent();
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevStatus = useRef(null);
  const timerRef = useRef(null);

  const load = () => api.get(`/orders/${id}`).then((r) => {
    const data = r.data;
    const prev = prevStatus.current;
    if (prev && prev !== data.status && TERMINAL_TOASTS[data.status]) {
      TERMINAL_TOASTS[data.status](t);
    }
    prevStatus.current = data.status;
    setO(data);
    setLoading(false);
    // Auto-stop polling once the order reaches a terminal state.
    if (!LIVE.includes(data.status) && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }).catch(() => setLoading(false));

  useEffect(() => {
    prevStatus.current = null;
    load();
    // Auto-refresh (backend only — the browser NEVER calls the provider API).
    timerRef.current = setInterval(load, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [id]);

  if (loading) return <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-accent" /></div>;
  if (!o) return <div className="py-20 text-center text-muted-foreground">{t("client.od.not_found", "Commande introuvable.")}</div>;

  const delivered = Math.max(0, (o.current_count || 0) - (o.start_count || 0));
  const pct = o.progress ?? 0;
  const live = LIVE.includes(o.status);
  const timeline = o.timeline || [];

  return (
    <div>
      <Link to="/dashboard/orders" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> {t("orders.detail.back", "Retour aux commandes")}</Link>
      <PageHeader title={`${t("client.od.order_num", "Commande #")}${o.order_number}`} subtitle={o.service_name} action={<StatusBadge status={o.status} />} />

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Panel title={t("orders.detail.progress_title", "Progression de la livraison")}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{delivered.toLocaleString()} / {(o.quantity || 0).toLocaleString()} {t("client.od.delivered", "livrés")}</span>
              <span className="font-display font-bold" data-testid="order-progress-value">{pct}% {live && <Loader2 className="ml-1 inline h-3 w-3 animate-spin" />}</span>
            </div>
            <Progress value={pct} className="mt-2" data-testid="order-progress" />
            <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
              <Metric label={t("client.od.start", "Départ")} value={(o.start_count || 0).toLocaleString()} />
              <Metric label={t("client.od.current", "Actuel")} value={(o.current_count || 0).toLocaleString()} />
              <Metric label={t("client.od.remaining", "Restant")} value={(o.remains || 0).toLocaleString()} />
            </div>
          </Panel>

          <Panel title={t("orders.detail.history_title", "Historique")}>
            <ol className="relative space-y-5 border-l border-border pl-6" data-testid="order-timeline">
              {timeline.map((s, i) => {
                const isLast = i === timeline.length - 1;
                return <TimelineStep key={s.key} step={s} isLast={isLast} live={live} />;
              })}
            </ol>
          </Panel>
        </div>

        <Panel title={t("orders.detail.details_title", "Détails")}>
          <dl className="space-y-3 text-sm">
            <Detail label={t("client.od.service", "Service")} value={o.service_name} />
            <Detail label={t("client.od.target", "Cible")} value={<span className="break-all">{o.target}</span>} />
            <Detail label={t("client.od.quantity", "Quantité")} value={(o.quantity || 0).toLocaleString()} />
            <Detail label={t("client.od.price", "Prix")} value={<span className="font-display font-bold"><Amount value={o.price} /></span>} />
            {o.refunded_amount > 0 && <Detail label={t("client.od.refunded", "Remboursé")} value={<span className="font-display font-bold text-amber-500"><Amount value={o.refunded_amount} /></span>} />}
            <Detail label={t("client.od.created_on", "Créée le")} value={fmtDate(o.order_created_at || o.created_at)} />
            <Detail label={t("client.od.updated", "Mise à jour")} value={fmtDate(o.updated_at)} />
          </dl>
        </Panel>
      </div>
    </div>
  );
}

const TONES = {
  done: { ring: "border-accent/40 bg-accent/10 text-accent", chip: "bg-muted text-muted-foreground", Icon: Check },
  active: { ring: "border-accent/40 bg-accent/10 text-accent", chip: "bg-accent/10 text-accent", Icon: Loader2 },
  success: { ring: "border-emerald-500/40 bg-emerald-500/10 text-emerald-500", chip: "bg-emerald-500/10 text-emerald-500", Icon: CheckCircle2 },
  warning: { ring: "border-amber-500/40 bg-amber-500/10 text-amber-500", chip: "bg-amber-500/10 text-amber-500", Icon: AlertTriangle },
  error: { ring: "border-red-500/40 bg-red-500/10 text-red-500", chip: "bg-red-500/10 text-red-500", Icon: XCircle },
};

const TimelineStep = ({ step, isLast, live }) => {
  const { t } = useContent();
  const tone = TONES[step.tone] || TONES.done;
  const spin = isLast && live && step.tone === "active";
  const Icon = spin ? Loader2 : tone.Icon;
  return (
    <li className="relative" data-testid={`timeline-${step.key}`}>
      <span className={`absolute -left-[33px] top-0 flex h-5 w-5 items-center justify-center rounded-full border ${tone.ring}`}>
        <Icon className={`h-3 w-3 ${spin ? "animate-spin" : ""}`} />
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{timelineLabel(t, step.key, step.label)}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.chip}`}>{statusLabel(t, step.status_label)}</span>
        {typeof step.progress === "number" && (
          <span className="text-xs font-display font-bold text-muted-foreground">{step.progress}%</span>
        )}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{fmtDate(step.at)}</div>
    </li>
  );
};

const Metric = ({ label, value }) => <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 font-display font-bold">{value}</div></div>;
const Detail = ({ label, value }) => <div className="flex justify-between gap-4 border-b border-border/60 pb-2 last:border-0"><dt className="text-muted-foreground">{label}</dt><dd className="text-right font-medium">{value}</dd></div>;
